import React, { useState } from "react";
import { ChevronDown, ChevronUp, ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion } from "lucide-react";

const LABEL_META = {
  "Strong Match":   { color: "#B5842A", bg: "bg-gold-50",  border: "border-gold-200", text: "text-gold-600", icon: ShieldCheck },
  "Good Match":     { color: "#0066FF", bg: "bg-brand-light", border: "border-brand",       text: "text-brand",       icon: ShieldCheck },
  "Partial Match":  { color: "#F59E0B", bg: "bg-amber-50",    border: "border-amber-200",   text: "text-amber-700",   icon: ShieldAlert },
  "Not Suitable":   { color: "#EF4444", bg: "bg-red-50",      border: "border-red-200",     text: "text-red-700",     icon: ShieldX },
};

const CATEGORY_LABELS = {
  licence: "Licence Status",
  role: "Role Match",
  qualifications: "Qualifications",
  experience: "Experience",
  kit: "Kit Available",
  location: "Location Match",
  availability: "Availability",
  endorsements: "Client Endorsements",
  completed_jobs: "Completed Jobs",
  reliability: "Reliability",
};

/**
 * Compact score card with expandable breakdown.
 * Props:
 *  - match: { score, label, breakdown?, weights?, override_delta? }
 *  - size: "sm" | "md" (default "sm")
 *  - showBreakdown: boolean (whether user can expand)
 */
export default function MatchScoreCard({ match, size = "sm", showBreakdown = true }) {
  const [open, setOpen] = useState(false);
  if (!match) return null;
  const meta = LABEL_META[match.label] || LABEL_META["Partial Match"];
  const Icon = meta.icon || ShieldQuestion;
  const score = Math.round(match.score);
  const isLg = size === "md";

  return (
    <div className={`rounded-lg border ${meta.border} ${meta.bg} ${isLg ? "p-4" : "p-3"}`} data-testid="match-score-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${meta.text} bg-white/60 flex-shrink-0`}>
            <Icon className="w-4 h-4" strokeWidth={2.4} />
          </div>
          <div className="min-w-0">
            <div className={`text-[10px] uppercase tracking-widest font-outfit font-bold ${meta.text}`}>Job Match Score</div>
            <div className={`font-outfit font-bold ${meta.text} ${isLg ? "text-lg" : "text-sm"} truncate`}>{match.label}</div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`font-outfit font-bold text-white ${isLg ? "text-3xl" : "text-2xl"} leading-none`} data-testid="match-score-value">
            {score}<span className="text-sm text-white/60 font-manrope">%</span>
          </div>
        </div>
      </div>

      {/* Bar */}
      <div className="mt-2 h-1.5 w-full rounded-full bg-white/60 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: meta.color }} />
      </div>

      {match.primary_reason && (match.label === "Not Suitable" || match.label === "Partial Match") && (
        <div
          className={`mt-2 rounded-md px-2.5 py-2 text-[11px] font-manrope bg-white/60 border ${meta.border}`}
          data-testid="match-score-primary-reason"
        >
          <span className={`font-outfit font-bold ${meta.text} uppercase tracking-widest text-[10px]`}>Why: </span>
          <span className="text-white">{match.primary_reason.message}</span>
        </div>
      )}

      {showBreakdown && match.breakdown && (
        <>
          <button
            className={`mt-2 text-xs font-manrope font-semibold ${meta.text} flex items-center gap-1`}
            onClick={() => setOpen((v) => !v)}
            data-testid="match-score-toggle"
            type="button"
          >
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {open ? "Hide breakdown" : "See score breakdown"}
          </button>
          {open && (
            <div className="mt-2 grid gap-1.5 text-[11px] font-manrope" data-testid="match-score-breakdown">
              {Object.entries(match.breakdown).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className="w-32 flex-shrink-0 text-white/70">{CATEGORY_LABELS[k] || k}</div>
                  <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${v}%`, background: meta.color }} />
                  </div>
                  <div className="w-10 text-right font-semibold text-white">{Math.round(v)}%</div>
                  {match.weights && (
                    <div className="w-10 text-right text-white/50">·{match.weights[k]}%</div>
                  )}
                </div>
              ))}
              {typeof match.override_delta === "number" && match.override_delta !== 0 && (
                <div className="mt-1 text-[11px] text-white/60">
                  Admin override applied: {match.override_delta > 0 ? "+" : ""}{match.override_delta.toFixed(1)} points
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
